import React, { useState, useRef, Suspense, useEffect } from "react";
import { Canvas, useFrame, useThree } from "@react-three/fiber";
import { OrbitControls, Text, Line } from "@react-three/drei";
import * as THREE from "three";
import {
  BuildingOfficeIcon,
  CheckCircleIcon,
  XCircleIcon,
  ExclamationTriangleIcon,
  ArrowLeftIcon,
  ArrowRightIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import Modal from "../../components/Modal";

interface Bed {
  id: number;
  bedNumber: string;
  status: "available" | "occupied" | "maintenance";
  patientName?: string;
  patientId?: number;
}

interface Room {
  id: number;
  roomNumber: string;
  roomType: string;
  floor: number;
  beds: Bed[];
  status: "available" | "occupied" | "maintenance";
  section: "nicu" | "waiting" | "normal" | "preparation";
  position: { x: number; z: number };
  size: { width: number; depth: number };
}

interface Floor {
  id: number;
  floorNumber: number;
  rooms: Room[];
}

// Room component in 3D
const Room3D: React.FC<{
  room: Room;
  onClick: (room: Room) => void;
  selected: boolean;
}> = ({ room, onClick, selected }) => {
  const meshRef = useRef<THREE.Mesh>(null);

  // Color based on status
  const getColor = () => {
    if (room.status === "occupied") return "#ef4444"; // red
    if (room.status === "maintenance") return "#f59e0b"; // yellow
    return "#10b981"; // green
  };

  // Section-based color tint
  const getSectionColor = () => {
    switch (room.section) {
      case "nicu":
        return "#3b82f6"; // blue tint
      case "waiting":
        return "#8b5cf6"; // purple tint
      case "preparation":
        return "#f97316"; // orange tint
      default:
        return "#ffffff"; // white
    }
  };

  const color = getColor();
  const sectionColor = getSectionColor();

  // Camera will pan to room instead of rotating

  const occupiedBeds = room.beds.filter((b) => b.status === "occupied").length;
  const totalBeds = room.beds.length;

  return (
    <group position={[room.position.x, 0, room.position.z]}>
      {/* Room floor */}
      <mesh
        ref={meshRef}
        position={[0, 0.1, 0]}
        onClick={(e) => {
          e.stopPropagation();
          onClick(room);
        }}
        onPointerOver={(e) => {
          e.stopPropagation();
          document.body.style.cursor = "pointer";
        }}
        onPointerOut={() => {
          document.body.style.cursor = "default";
        }}
      >
        <boxGeometry args={[room.size.width, 0.2, room.size.depth]} />
        <meshStandardMaterial
          color={selected ? "#6366f1" : color}
          opacity={0.8}
          transparent
        />
      </mesh>

      {/* Room walls - 3 solid sides (back, left, right), front and top open */}
      {/* Back wall */}
      <mesh position={[0, 0.6, -room.size.depth / 2]}>
        <boxGeometry args={[room.size.width, 1.2, 0.1]} />
        <meshStandardMaterial color={sectionColor} />
      </mesh>
      
      {/* Left wall */}
      <mesh position={[-room.size.width / 2, 0.6, 0]}>
        <boxGeometry args={[0.1, 1.2, room.size.depth]} />
        <meshStandardMaterial color={sectionColor} />
      </mesh>
      
      {/* Right wall */}
      <mesh position={[room.size.width / 2, 0.6, 0]}>
        <boxGeometry args={[0.1, 1.2, room.size.depth]} />
        <meshStandardMaterial color={sectionColor} />
      </mesh>
      
      {/* Front is open - no wall */}
      {/* Top is open - no ceiling */}

      {/* Room label - fixed on floor */}
      <Text
        position={[0, 0.02, 0]}
        fontSize={0.25}
        color="#1f2937"
        anchorX="center"
        anchorY="middle"
        rotation={[-Math.PI / 2, 0, 0]}
      >
        {room.roomNumber}
      </Text>

      {/* Bed count label - fixed on floor */}
      <Text
        position={[0, 0.02, -room.size.depth / 2 + 0.3]}
        fontSize={0.15}
        color="#4b5563"
        anchorX="center"
        anchorY="middle"
        rotation={[-Math.PI / 2, 0, 0]}
      >
        {occupiedBeds}/{totalBeds}
      </Text>

      {/* Beds visualization */}
      {room.beds.map((bed, index) => {
        const bedX = (index % 2) * 0.8 - 0.4;
        const bedZ = Math.floor(index / 2) * 0.8 - 0.4;
        const bedColor =
          bed.status === "occupied"
            ? "#dc2626"
            : bed.status === "maintenance"
            ? "#d97706"
            : "#16a34a";

        return (
          <mesh
            key={bed.id}
            position={[bedX, 0.3, bedZ]}
            onClick={(e) => {
              e.stopPropagation();
              onClick(room);
            }}
          >
            <boxGeometry args={[0.3, 0.2, 0.5]} />
            <meshStandardMaterial color={bedColor} />
          </mesh>
        );
      })}
    </group>
  );
};

// Camera controller component to animate to selected room
const CameraController: React.FC<{
  selectedRoom: Room | null;
  onDeselect: () => void;
  controlsRef: React.MutableRefObject<any>;
}> = ({ selectedRoom, onDeselect, controlsRef }) => {
  const { camera } = useThree();

  useEffect(() => {
    if (selectedRoom && controlsRef.current) {
      // Calculate top-down view position
      const targetX = selectedRoom.position.x;
      const targetZ = selectedRoom.position.z;
      const targetY = 8; // Height for top-down view
      
      // Animate camera to room position (top-down view)
      const startPos = camera.position.clone();
      const targetPos = new THREE.Vector3(targetX, targetY, targetZ + 5);
      
      // Set target for OrbitControls
      controlsRef.current.target.set(targetX, 0, targetZ);
      
      let startTime: number | null = null;
      const duration = 1000; // 1 second animation
      
      const animate = (timestamp: number) => {
        if (!startTime) startTime = timestamp;
        const progress = Math.min((timestamp - startTime) / duration, 1);
        
        // Easing function (ease-in-out)
        const eased = progress < 0.5
          ? 2 * progress * progress
          : 1 - Math.pow(-2 * progress + 2, 2) / 2;
        
        camera.position.lerpVectors(startPos, targetPos, eased);
        controlsRef.current.update();
        
        if (progress < 1) {
          requestAnimationFrame(animate);
        }
      };
      
      requestAnimationFrame(animate);
    }
  }, [selectedRoom, camera, controlsRef]);

  // Handle ESC key to deselect
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && selectedRoom) {
        onDeselect();
      }
    };
    
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [selectedRoom, onDeselect]);

  return null;
};

// Floor plan component
const FloorPlan: React.FC<{
  rooms: Room[];
  selectedRoom: Room | null;
  onRoomClick: (room: Room) => void;
  onDeselect: () => void;
}> = ({ rooms, selectedRoom, onRoomClick, onDeselect }) => {
  // Calculate department boundaries
  const getDepartmentBounds = (section: Room["section"]) => {
    const sectionRooms = rooms.filter((r) => r.section === section);
    if (sectionRooms.length === 0) return null;

    let minX = Infinity,
      maxX = -Infinity,
      minZ = Infinity,
      maxZ = -Infinity;

    sectionRooms.forEach((room) => {
      const halfWidth = room.size.width / 2;
      const halfDepth = room.size.depth / 2;
      minX = Math.min(minX, room.position.x - halfWidth);
      maxX = Math.max(maxX, room.position.x + halfWidth);
      minZ = Math.min(minZ, room.position.z - halfDepth);
      maxZ = Math.max(maxZ, room.position.z + halfDepth);
    });

    // Add padding
    const padding = 1;
    return {
      minX: minX - padding,
      maxX: maxX + padding,
      minZ: minZ - padding,
      maxZ: maxZ + padding,
    };
  };

  const nicuBounds = getDepartmentBounds("nicu");
  const waitingBounds = getDepartmentBounds("waiting");
  const preparationBounds = getDepartmentBounds("preparation");
  const normalBounds = getDepartmentBounds("normal");

  const drawBorder = (bounds: { minX: number; maxX: number; minZ: number; maxZ: number } | null, color: string) => {
    if (!bounds) return null;
    const { minX, maxX, minZ, maxZ } = bounds;
    const borderHeight = 0.03;
    const borderThickness = 0.15;
    return (
      <>
        {/* Top border */}
        <mesh position={[(minX + maxX) / 2, borderHeight, minZ]}>
          <boxGeometry args={[maxX - minX, borderThickness, borderThickness]} />
          <meshStandardMaterial color={color} />
        </mesh>
        {/* Right border */}
        <mesh position={[maxX, borderHeight, (minZ + maxZ) / 2]}>
          <boxGeometry args={[borderThickness, borderThickness, maxZ - minZ]} />
          <meshStandardMaterial color={color} />
        </mesh>
        {/* Bottom border */}
        <mesh position={[(minX + maxX) / 2, borderHeight, maxZ]}>
          <boxGeometry args={[maxX - minX, borderThickness, borderThickness]} />
          <meshStandardMaterial color={color} />
        </mesh>
        {/* Left border */}
        <mesh position={[minX, borderHeight, (minZ + maxZ) / 2]}>
          <boxGeometry args={[borderThickness, borderThickness, maxZ - minZ]} />
          <meshStandardMaterial color={color} />
        </mesh>
      </>
    );
  };

  return (
    <>
      {/* Clickable floor to deselect */}
      <mesh
        rotation={[-Math.PI / 2, 0, 0]}
        position={[0, 0, 0]}
        onClick={(e) => {
          if (e.detail === 1) {
            // Only deselect on single click, not double click
            setTimeout(() => {
              if (selectedRoom) {
                onDeselect();
              }
            }, 100);
          }
        }}
      >
        <planeGeometry args={[30, 30]} />
        <meshStandardMaterial color="#f3f4f6" transparent opacity={0.01} />
      </mesh>
      
      {/* Visible floor */}
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, 0, 0]}>
        <planeGeometry args={[30, 30]} />
        <meshStandardMaterial color="#f3f4f6" />
      </mesh>

      {/* Grid lines */}
      {Array.from({ length: 31 }, (_, i) => i - 15).map((x) => (
        <Line
          key={`grid-x-${x}`}
          points={[
            [x, 0.01, -15],
            [x, 0.01, 15],
          ]}
          color="#e5e7eb"
          opacity={0.3}
          transparent
        />
      ))}
      {Array.from({ length: 31 }, (_, i) => i - 15).map((z) => (
        <Line
          key={`grid-z-${z}`}
          points={[
            [-15, 0.01, z],
            [15, 0.01, z],
          ]}
          color="#e5e7eb"
          opacity={0.3}
          transparent
        />
      ))}

      {/* Department borders - on top of grid */}
      {drawBorder(nicuBounds, "#3b82f6")}
      {drawBorder(waitingBounds, "#8b5cf6")}
      {drawBorder(preparationBounds, "#f97316")}
      {drawBorder(normalBounds, "#10b981")}

      {/* Rooms */}
      {rooms.map((room) => (
        <Room3D
          key={room.id}
          room={room}
          onClick={onRoomClick}
          selected={selectedRoom?.id === room.id}
        />
      ))}

      {/* Section labels - fixed on floor */}
      {nicuBounds && (
        <Text
          position={[(nicuBounds.minX + nicuBounds.maxX) / 2, 0.02, nicuBounds.minZ + 0.5]}
          fontSize={0.4}
          color="#3b82f6"
          anchorX="center"
          anchorY="middle"
          rotation={[-Math.PI / 2, 0, 0]}
        >
          NICU Section
        </Text>
      )}
      {waitingBounds && (
        <Text
          position={[(waitingBounds.minX + waitingBounds.maxX) / 2, 0.02, waitingBounds.minZ + 0.5]}
          fontSize={0.4}
          color="#8b5cf6"
          anchorX="center"
          anchorY="middle"
          rotation={[-Math.PI / 2, 0, 0]}
        >
          Waiting Area
        </Text>
      )}
      {preparationBounds && (
        <Text
          position={[(preparationBounds.minX + preparationBounds.maxX) / 2, 0.02, preparationBounds.minZ + 0.5]}
          fontSize={0.4}
          color="#f97316"
          anchorX="center"
          anchorY="middle"
          rotation={[-Math.PI / 2, 0, 0]}
        >
          Preparation Rooms
        </Text>
      )}
      {normalBounds && (
        <Text
          position={[(normalBounds.minX + normalBounds.maxX) / 2, 0.02, normalBounds.minZ + 0.5]}
          fontSize={0.4}
          color="#10b981"
          anchorX="center"
          anchorY="middle"
          rotation={[-Math.PI / 2, 0, 0]}
        >
          Normal Rooms
        </Text>
      )}
    </>
  );
};

// Main component
const BedLayout3D: React.FC = () => {
  // Mock data with realistic floor plans
  const floorsData: Floor[] = [
    {
      id: 1,
      floorNumber: 1,
      rooms: [
        // NICU Section (left side)
        {
          id: 1,
          roomNumber: "NICU-101",
          roomType: "NICU",
          floor: 1,
          status: "occupied",
          section: "nicu",
          position: { x: -8, z: -8 },
          size: { width: 3, depth: 3 },
          beds: [
            { id: 1, bedNumber: "A", status: "occupied", patientName: "Baby A", patientId: 1 },
            { id: 2, bedNumber: "B", status: "occupied", patientName: "Baby B", patientId: 2 },
          ],
        },
        {
          id: 2,
          roomNumber: "NICU-102",
          roomType: "NICU",
          floor: 1,
          status: "occupied",
          section: "nicu",
          position: { x: -4, z: -8 },
          size: { width: 3, depth: 3 },
          beds: [
            { id: 3, bedNumber: "A", status: "occupied", patientName: "Baby C", patientId: 3 },
            { id: 4, bedNumber: "B", status: "available" },
          ],
        },
        {
          id: 3,
          roomNumber: "NICU-103",
          roomType: "NICU",
          floor: 1,
          status: "available",
          section: "nicu",
          position: { x: 0, z: -8 },
          size: { width: 3, depth: 3 },
          beds: [
            { id: 5, bedNumber: "A", status: "available" },
            { id: 6, bedNumber: "B", status: "available" },
          ],
        },
        // Preparation/Rest Rooms near NICU
        {
          id: 4,
          roomNumber: "PREP-101",
          roomType: "Preparation",
          floor: 1,
          status: "available",
          section: "preparation",
          position: { x: -8, z: -4 },
          size: { width: 2, depth: 2 },
          beds: [],
        },
        {
          id: 5,
          roomNumber: "PREP-102",
          roomType: "Rest",
          floor: 1,
          status: "available",
          section: "preparation",
          position: { x: -4, z: -4 },
          size: { width: 2, depth: 2 },
          beds: [],
        },
        // Waiting Area (right side)
        {
          id: 6,
          roomNumber: "WAIT-101",
          roomType: "Waiting",
          floor: 1,
          status: "available",
          section: "waiting",
          position: { x: 8, z: -8 },
          size: { width: 4, depth: 4 },
          beds: [],
        },
        {
          id: 7,
          roomNumber: "WAIT-102",
          roomType: "Waiting",
          floor: 1,
          status: "available",
          section: "waiting",
          position: { x: 4, z: -8 },
          size: { width: 4, depth: 4 },
          beds: [],
        },
        // Normal Rooms (bottom area)
        {
          id: 8,
          roomNumber: "101",
          roomType: "Single",
          floor: 1,
          status: "occupied",
          section: "normal",
          position: { x: -8, z: 4 },
          size: { width: 2.5, depth: 2.5 },
          beds: [
            { id: 7, bedNumber: "A", status: "occupied", patientName: "John Doe", patientId: 4 },
          ],
        },
        {
          id: 9,
          roomNumber: "102",
          roomType: "Double",
          floor: 1,
          status: "occupied",
          section: "normal",
          position: { x: -4, z: 4 },
          size: { width: 2.5, depth: 2.5 },
          beds: [
            { id: 8, bedNumber: "A", status: "occupied", patientName: "Jane Smith", patientId: 5 },
            { id: 9, bedNumber: "B", status: "occupied", patientName: "Bob Johnson", patientId: 6 },
          ],
        },
        {
          id: 10,
          roomNumber: "103",
          roomType: "Single",
          floor: 1,
          status: "available",
          section: "normal",
          position: { x: 0, z: 4 },
          size: { width: 2.5, depth: 2.5 },
          beds: [
            { id: 10, bedNumber: "A", status: "available" },
          ],
        },
        {
          id: 11,
          roomNumber: "104",
          roomType: "Deluxe",
          floor: 1,
          status: "maintenance",
          section: "normal",
          position: { x: 4, z: 4 },
          size: { width: 3, depth: 3 },
          beds: [
            { id: 11, bedNumber: "A", status: "maintenance" },
            { id: 12, bedNumber: "B", status: "maintenance" },
            { id: 13, bedNumber: "C", status: "maintenance" },
          ],
        },
        {
          id: 12,
          roomNumber: "105",
          roomType: "Double",
          floor: 1,
          status: "available",
          section: "normal",
          position: { x: 8, z: 4 },
          size: { width: 2.5, depth: 2.5 },
          beds: [
            { id: 14, bedNumber: "A", status: "available" },
            { id: 15, bedNumber: "B", status: "available" },
          ],
        },
      ],
    },
    {
      id: 2,
      floorNumber: 2,
      rooms: [
        // Floor 2: 3 rooms on left, 2 rooms on right
        {
          id: 13,
          roomNumber: "201",
          roomType: "Single",
          floor: 2,
          status: "occupied",
          section: "normal",
          position: { x: -8, z: -4 },
          size: { width: 2.5, depth: 2.5 },
          beds: [
            { id: 16, bedNumber: "A", status: "occupied", patientName: "Alice Brown", patientId: 7 },
          ],
        },
        {
          id: 14,
          roomNumber: "202",
          roomType: "Double",
          floor: 2,
          status: "occupied",
          section: "normal",
          position: { x: -4, z: -4 },
          size: { width: 2.5, depth: 2.5 },
          beds: [
            { id: 17, bedNumber: "A", status: "occupied", patientName: "Charlie Wilson", patientId: 8 },
            { id: 18, bedNumber: "B", status: "available" },
          ],
        },
        {
          id: 15,
          roomNumber: "203",
          roomType: "Single",
          floor: 2,
          status: "available",
          section: "normal",
          position: { x: 0, z: -4 },
          size: { width: 2.5, depth: 2.5 },
          beds: [
            { id: 19, bedNumber: "A", status: "available" },
          ],
        },
        {
          id: 16,
          roomNumber: "204",
          roomType: "Deluxe",
          floor: 2,
          status: "occupied",
          section: "normal",
          position: { x: 6, z: -4 },
          size: { width: 3, depth: 3 },
          beds: [
            { id: 20, bedNumber: "A", status: "occupied", patientName: "Diana Prince", patientId: 9 },
            { id: 21, bedNumber: "B", status: "occupied", patientName: "Edward Lee", patientId: 10 },
            { id: 22, bedNumber: "C", status: "available" },
          ],
        },
        {
          id: 17,
          roomNumber: "205",
          roomType: "Double",
          floor: 2,
          status: "available",
          section: "normal",
          position: { x: 10, z: -4 },
          size: { width: 2.5, depth: 2.5 },
          beds: [
            { id: 23, bedNumber: "A", status: "available" },
            { id: 24, bedNumber: "B", status: "available" },
          ],
        },
      ],
    },
  ];

  const [selectedFloor, setSelectedFloor] = useState<number>(1);
  const [selectedRoom, setSelectedRoom] = useState<Room | null>(null);
  const controlsRef = useRef<any>(null);

  const currentFloor = floorsData.find((f) => f.floorNumber === selectedFloor) || floorsData[0];

  const totalStats = React.useMemo(() => {
    let totalBeds = 0;
    let availableBeds = 0;
    let occupiedBeds = 0;
    let maintenanceBeds = 0;

    floorsData.forEach((floor) => {
      floor.rooms.forEach((room) => {
        room.beds.forEach((bed) => {
          totalBeds++;
          if (bed.status === "available") availableBeds++;
          else if (bed.status === "occupied") occupiedBeds++;
          else if (bed.status === "maintenance") maintenanceBeds++;
        });
      });
    });

    return { totalBeds, availableBeds, occupiedBeds, maintenanceBeds };
  }, []);

  const handleRoomClick = (room: Room) => {
    // Toggle selection - if clicking the same room, deselect it
    if (selectedRoom?.id === room.id) {
      setSelectedRoom(null);
    } else {
      setSelectedRoom(room);
    }
  };

  const handleDeselect = () => {
    setSelectedRoom(null);
  };

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-6 h-screen flex flex-col">
      {/* Header */}
      <div className="mb-4">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <BuildingOfficeIcon className="w-7 h-7 text-primary" />
              3D Hospital Bed Layout
            </h1>
            <p className="mt-1 text-sm text-gray-600">
              Interactive 3D visualization of hospital floor plans
            </p>
          </div>
        </div>

        {/* Statistics Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 mb-4">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Beds</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">{totalStats.totalBeds}</p>
              </div>
              <div className="bg-blue-100 rounded-full p-3">
                <BuildingOfficeIcon className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Available</p>
                <p className="text-2xl font-bold text-green-600 mt-1">{totalStats.availableBeds}</p>
              </div>
              <div className="bg-green-100 rounded-full p-3">
                <CheckCircleIcon className="w-6 h-6 text-green-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Occupied</p>
                <p className="text-2xl font-bold text-red-600 mt-1">{totalStats.occupiedBeds}</p>
              </div>
              <div className="bg-red-100 rounded-full p-3">
                <XCircleIcon className="w-6 h-6 text-red-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Maintenance</p>
                <p className="text-2xl font-bold text-yellow-600 mt-1">{totalStats.maintenanceBeds}</p>
              </div>
              <div className="bg-yellow-100 rounded-full p-3">
                <ExclamationTriangleIcon className="w-6 h-6 text-yellow-600" />
              </div>
            </div>
          </div>
        </div>

        {/* Floor Selector */}
        <div className="flex items-center gap-2 mb-4">
          <span className="text-sm font-semibold text-gray-700">Floor:</span>
          <button
            onClick={() => {
              if (selectedFloor > 1) setSelectedFloor(selectedFloor - 1);
            }}
            disabled={selectedFloor === 1}
            className="p-2 rounded-lg bg-white border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ArrowLeftIcon className="w-5 h-5" />
          </button>
          <div className="flex gap-2">
            {floorsData.map((floor) => (
              <button
                key={floor.id}
                onClick={() => {
                  setSelectedFloor(floor.floorNumber);
                  setSelectedRoom(null);
                }}
                className={`px-4 py-2 rounded-lg font-medium transition-all duration-200 ${
                  selectedFloor === floor.floorNumber
                    ? "bg-primary text-white shadow-md"
                    : "bg-white text-gray-700 border border-gray-300 hover:bg-gray-50"
                }`}
              >
                Floor {floor.floorNumber}
              </button>
            ))}
          </div>
          <button
            onClick={() => {
              if (selectedFloor < floorsData.length) setSelectedFloor(selectedFloor + 1);
            }}
            disabled={selectedFloor === floorsData.length}
            className="p-2 rounded-lg bg-white border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ArrowRightIcon className="w-5 h-5" />
          </button>
        </div>

        {/* Legend */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-4">
          <p className="text-sm font-semibold text-gray-700 mb-3">Legend:</p>
          <div className="flex flex-wrap gap-4">
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded border-2 border-green-400 bg-green-100"></div>
              <span className="text-sm text-gray-600">Available</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded border-2 border-red-400 bg-red-100"></div>
              <span className="text-sm text-gray-600">Occupied</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded border-2 border-yellow-400 bg-yellow-100"></div>
              <span className="text-sm text-gray-600">Maintenance</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded border-2 border-blue-400 bg-blue-100"></div>
              <span className="text-sm text-gray-600">NICU</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded border-2 border-purple-400 bg-purple-100"></div>
              <span className="text-sm text-gray-600">Waiting Area</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded border-2 border-orange-400 bg-orange-100"></div>
              <span className="text-sm text-gray-600">Preparation</span>
            </div>
          </div>
        </div>
      </div>

      {/* 3D Canvas */}
      <div className="flex-1 bg-gray-100 rounded-xl overflow-hidden border border-gray-200 relative">
        <Canvas
          camera={{ position: [15, 15, 15], fov: 50 }}
          style={{ width: "100%", height: "100%" }}
        >
          <Suspense fallback={null}>
            <ambientLight intensity={0.5} />
            <directionalLight position={[10, 10, 5]} intensity={1} />
            <pointLight position={[-10, 10, -10]} intensity={0.5} />
            <CameraController selectedRoom={selectedRoom} onDeselect={handleDeselect} controlsRef={controlsRef} />
            <FloorPlan
              rooms={currentFloor.rooms}
              selectedRoom={selectedRoom}
              onRoomClick={handleRoomClick}
              onDeselect={handleDeselect}
            />
            <OrbitControls
              ref={controlsRef}
              enablePan={true}
              enableZoom={true}
              enableRotate={true}
              minDistance={2}
              maxDistance={100}
            />
          </Suspense>
        </Canvas>

        {/* Instructions overlay */}
        <div className="absolute top-4 right-4 bg-white/90 backdrop-blur-sm rounded-lg p-3 shadow-lg border border-gray-200">
          <p className="text-xs font-semibold text-gray-700 mb-2">Controls:</p>
          <ul className="text-xs text-gray-600 space-y-1">
            <li>• Click and drag to rotate</li>
            <li>• Scroll to zoom</li>
            <li>• Right-click + drag to pan</li>
            <li>• Click rooms to select</li>
            <li>• Click floor or ESC to deselect</li>
          </ul>
        </div>
      </div>

      {/* Full Screen Modal with 3D Canvas and Room Details */}
      {selectedRoom && (
        <Modal
          width="max-w-full"
          onClose={handleDeselect}
          modalContent={
            <div className="flex h-[90vh] w-full">
              {/* 3D Canvas - Takes up most of the space */}
              <div className="flex-1 bg-gray-100 relative">
                <Canvas
                  camera={{ position: [15, 15, 15], fov: 50 }}
                  style={{ width: "100%", height: "100%" }}
                >
                  <Suspense fallback={null}>
                    <ambientLight intensity={0.5} />
                    <directionalLight position={[10, 10, 5]} intensity={1} />
                    <pointLight position={[-10, 10, -10]} intensity={0.5} />
                    <CameraController selectedRoom={selectedRoom} onDeselect={handleDeselect} controlsRef={controlsRef} />
                    <FloorPlan
                      rooms={currentFloor.rooms}
                      selectedRoom={selectedRoom}
                      onRoomClick={handleRoomClick}
                      onDeselect={handleDeselect}
                    />
                    <OrbitControls
                      ref={controlsRef}
                      enablePan={true}
                      enableZoom={true}
                      enableRotate={true}
                      minDistance={2}
                      maxDistance={100}
                    />
                  </Suspense>
                </Canvas>

                {/* Instructions overlay */}
                <div className="absolute top-4 right-4 bg-white/90 backdrop-blur-sm rounded-lg p-3 shadow-lg border border-gray-200">
                  <p className="text-xs font-semibold text-gray-700 mb-2">Controls:</p>
                  <ul className="text-xs text-gray-600 space-y-1">
                    <li>• Click and drag to rotate</li>
                    <li>• Scroll to zoom</li>
                    <li>• Right-click + drag to pan</li>
                    <li>• Click floor or ESC to close</li>
                  </ul>
                </div>
              </div>

              {/* Room Details Sidebar - Right side */}
              <div className="w-96 bg-white border-l border-gray-200 overflow-y-auto">
                <div className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-xl font-bold text-gray-900">
                      Room {selectedRoom.roomNumber}
                    </h3>
                    <button
                      onClick={handleDeselect}
                      className="p-2 text-gray-400 hover:text-gray-600 rounded-md hover:bg-gray-100 transition-colors"
                    >
                      <XMarkIcon className="w-5 h-5" />
                    </button>
                  </div>
                  
                  <div className="mb-4">
                    <span className="text-sm font-medium text-gray-600">Room Type:</span>
                    <p className="text-lg text-gray-900">{selectedRoom.roomType}</p>
                  </div>

                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div>
                      <span className="text-sm font-medium text-gray-600">Status:</span>
                      <p className="text-gray-900 capitalize font-semibold">{selectedRoom.status}</p>
                    </div>
                    <div>
                      <span className="text-sm font-medium text-gray-600">Section:</span>
                      <p className="text-gray-900 capitalize">{selectedRoom.section}</p>
                    </div>
                    <div>
                      <span className="text-sm font-medium text-gray-600">Floor:</span>
                      <p className="text-gray-900">{selectedRoom.floor}</p>
                    </div>
                    <div>
                      <span className="text-sm font-medium text-gray-600">Beds:</span>
                      <p className="text-gray-900">
                        {selectedRoom.beds.filter((b) => b.status === "occupied").length} /{" "}
                        {selectedRoom.beds.length} occupied
                      </p>
                    </div>
                  </div>

                  {selectedRoom.beds.length > 0 && (
                    <div>
                      <h4 className="text-sm font-semibold text-gray-700 mb-3">Beds:</h4>
                      <div className="space-y-2">
                        {selectedRoom.beds.map((bed) => (
                          <div
                            key={bed.id}
                            className={`p-3 rounded-lg border ${
                              bed.status === "occupied"
                                ? "bg-red-50 border-red-200"
                                : bed.status === "maintenance"
                                ? "bg-yellow-50 border-yellow-200"
                                : "bg-green-50 border-green-200"
                            }`}
                          >
                            <div className="flex items-center justify-between">
                              <div>
                                <p className="text-sm font-semibold text-gray-900">Bed {bed.bedNumber}</p>
                                <p className="text-xs text-gray-600 capitalize mt-1">{bed.status}</p>
                              </div>
                              <div>
                                {bed.status === "occupied" && (
                                  <CheckCircleIcon className="w-5 h-5 text-red-600" />
                                )}
                                {bed.status === "maintenance" && (
                                  <ExclamationTriangleIcon className="w-5 h-5 text-yellow-600" />
                                )}
                                {bed.status === "available" && (
                                  <CheckCircleIcon className="w-5 h-5 text-green-600" />
                                )}
                              </div>
                            </div>
                            {bed.patientName && (
                              <p className="text-sm font-medium text-gray-900 mt-2">
                                Patient: {bed.patientName}
                              </p>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          }
        />
      )}
    </div>
  );
};

export default BedLayout3D;

